import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const languages = [
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'fr', label: 'Français', flag: '🇫🇷' },
    { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
    { code: 'da', label: 'Dansk', flag: '🇩🇰' },
  ];

  const currentLang = languages.find((l) => l.code === i18n.language) || languages[0];

  const closeAll = () => {
    setDropdownOpen(false);
    setLangOpen(false);
    setMobileOpen(false);
  };

  return (
    <nav className="sticky top-0 z-50 bg-white border-b-2 border-background-200">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link to="/" onClick={closeAll} className="flex items-center gap-2 shrink-0">
            <span className="font-heading text-lg md:text-xl font-bold text-foreground-950 tracking-tight">
              AllChinaBuy
            </span>
            <span className="hidden sm:inline text-xs font-semibold px-2 py-0.5 rounded-full bg-accent-100 text-accent-900 border border-accent-200">
              Spreadsheet
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-5 lg:gap-7">
            <Link to="/" onClick={closeAll} className="text-sm font-semibold text-foreground-900 hover:text-accent-600 transition-colors whitespace-nowrap">
              {t('nav_home')}
            </Link>

            <Link to="/allchinabuy-spreadsheet-guide" onClick={closeAll} className="text-sm font-semibold text-foreground-900 hover:text-accent-600 transition-colors whitespace-nowrap">
              {t('nav_shopping_guide')}
            </Link>

            {/* Categories Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1 text-sm font-semibold text-foreground-900 hover:text-accent-600 transition-colors whitespace-nowrap"
              >
                {t('nav_categories')}
                <i className={`ri-arrow-down-s-line transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg border border-background-200 py-2 z-50 max-h-96 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-800 hover:bg-background-100 hover:text-foreground-950 transition-colors"
                    >
                      <img src={cat.imageUrl} alt="" className="w-9 h-9 rounded object-cover shrink-0" />
                      <span className="font-medium">{t(cat.nameKey)}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm font-semibold text-foreground-900 hover:text-accent-600 transition-colors whitespace-nowrap">
              {t('nav_shipping')}
            </a>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm font-semibold text-foreground-900 hover:text-accent-600 transition-colors whitespace-nowrap">
              {t('nav_qc')}
            </a>
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-2 md:gap-3">
            {/* Language Switcher */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1.5 text-sm font-semibold text-foreground-900 hover:text-accent-600 px-2 md:px-3 py-1.5 rounded-md hover:bg-background-100 transition-colors"
              >
                <span className="text-base">{currentLang.flag}</span>
                <span className="hidden sm:inline uppercase text-xs font-semibold">{currentLang.code}</span>
                <i className={`ri-arrow-down-s-line transition-transform ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              {langOpen && (
                <div className="absolute top-full right-0 mt-2 w-44 bg-white rounded-lg border border-background-200 py-2 z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        i18n.changeLanguage(lang.code);
                        setLangOpen(false);
                      }}
                      className={`flex items-center gap-3 w-full px-4 py-2.5 text-sm transition-colors ${
                        i18n.language === lang.code
                          ? 'bg-primary-50 text-primary-700 font-semibold'
                          : 'text-foreground-800 hover:bg-background-100'
                      }`}
                    >
                      <span className="text-base">{lang.flag}</span>
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* CTA */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-accent-500 text-background-50 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap"
            >
              {t('nav_cta')}
              <i className="ri-external-link-line text-xs" />
            </a>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden p-2 text-foreground-900 hover:text-accent-600 transition-colors"
              aria-label="Menu"
            >
              <i className={`text-2xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-white max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            <Link
              to="/"
              onClick={closeAll}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-foreground-900 font-semibold hover:bg-background-100 transition-colors"
            >
              <i className="ri-home-4-line text-lg text-accent-500" />
              {t('nav_home')}
            </Link>
            <Link
              to="/allchinabuy-spreadsheet-guide"
              onClick={closeAll}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-foreground-900 font-semibold hover:bg-background-100 transition-colors"
            >
              <i className="ri-book-open-line text-lg text-accent-500" />
              {t('nav_shopping_guide')}
            </Link>

            <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-foreground-400">
              {t('nav_categories')}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeAll}
                  className="flex items-center gap-2 px-2 py-2 rounded-lg text-foreground-800 hover:bg-background-100 transition-colors"
                >
                  <img src={cat.imageUrl} alt="" className="w-8 h-8 rounded object-cover shrink-0" />
                  <span className="text-sm font-medium leading-tight">{t(cat.nameKey)}</span>
                </a>
              ))}
            </div>

            <div className="border-t border-background-200 my-2" />

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={closeAll}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-foreground-900 font-semibold hover:bg-background-100 transition-colors"
            >
              <i className="ri-truck-line text-lg text-accent-500" />
              {t('nav_shipping')}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={closeAll}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-foreground-900 font-semibold hover:bg-background-100 transition-colors"
            >
              <i className="ri-shield-check-line text-lg text-accent-500" />
              {t('nav_qc')}
            </a>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={closeAll}
              className="mt-2 flex items-center justify-center gap-2 px-4 py-3 bg-accent-500 text-background-50 rounded-lg font-semibold text-sm hover:bg-accent-600 transition-colors"
            >
              {t('nav_cta')}
              <i className="ri-external-link-line text-xs" />
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}